import { Component, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { VersionService, SolarData, SolarHistoryPoint, DayStat } from '../version.service';
import { formatW, formatKwh, updatedAgo as fmtAgo } from '../shared/format.utils';

@Component({
  selector: 'app-energie',
  templateUrl: './energie.component.html',
})
export class EnergieComponent {
  @Input() solar:           SolarData | null        = null;
  @Input() solarHistory:    SolarHistoryPoint[]     = [];
  @Input() solarFlash       = false;
  @Input() liveDotOn        = true;
  @Input() solarUpdatedAt:  number | null           = null;
  @Input() nowTick          = Date.now();
  @Input() todayStat:       DayStat | null          = null;
  @Input() darkMode         = false;

  @Output() openSolarChart  = new EventEmitter<void>();

  constructor(readonly translate: TranslateService, readonly vs: VersionService) {}

  readonly Math = Math;
  formatW   = formatW;
  formatKwh = formatKwh;

  // Per-tab time range: 'day' uses the live 24h history; longer ranges fetch the
  // downsampled long-term archive and drive every solar chart on this tab.
  range: 'day' | 'week' | 'month' | 'year' = 'day';
  rangeLoading = false;
  solarRangeHistory: SolarHistoryPoint[] = [];
  readonly chartRanges: { key: 'day' | 'week' | 'month' | 'year'; label: string }[] = [
    { key: 'day',   label: 'Tag' },
    { key: 'week',  label: 'Woche' },
    { key: 'month', label: 'Monat' },
    { key: 'year',  label: 'Jahr' },
  ];
  private static readonly RANGE_MS: Record<string, number> = {
    day:   24 * 3600 * 1000,
    week:  7 * 24 * 3600 * 1000,
    month: 30 * 24 * 3600 * 1000,
    year:  365 * 24 * 3600 * 1000,
  };

  get displaySolar(): SolarHistoryPoint[] {
    return this.range === 'day' ? this.solarHistory : this.solarRangeHistory;
  }

  get rangeLabel(): string {
    return this.chartRanges.find(r => r.key === this.range)?.label ?? '';
  }

  get windowMs(): number {
    return EnergieComponent.RANGE_MS[this.range];
  }

  setRange(range: 'day' | 'week' | 'month' | 'year'): void {
    this.range = range;
    if (range === 'day') return;
    const to   = Date.now();
    const from = to - EnergieComponent.RANGE_MS[range];
    this.rangeLoading = true;
    fetch(`/api/history?series=solar&from=${from}&to=${to}&buckets=600`)
      .then(r => r.json())
      .then((pts: any[]) => { this.solarRangeHistory = pts ?? []; this.rangeLoading = false; })
      .catch(() => { this.rangeLoading = false; });
  }

  private readonly BATTERY_BARRIER_W = 7360;
  private readonly GRID_BARRIER_W    = 3680;
  private readonly phaseColors = ['#0d6efd', '#6610f2', '#d63384'];

  hasPhaseData(solar: SolarData): boolean {
    return solar.consumptionL1W > 0 || solar.consumptionL2W > 0 || solar.consumptionL3W > 0;
  }

  phaseData(solar: SolarData): {label: string; watts: number; pct: number; gridBarrierPct: number; color: string}[] {
    const phases = [
      { label: 'L1', watts: solar.consumptionL1W },
      { label: 'L2', watts: solar.consumptionL2W },
      { label: 'L3', watts: solar.consumptionL3W },
    ];
    const max = this.BATTERY_BARRIER_W;
    return phases.map((p, i) => ({
      ...p,
      pct: Math.min(100, p.watts / max * 100),
      gridBarrierPct: this.GRID_BARRIER_W / max * 100,
      color: this.phaseColors[i],
    }));
  }

  solarMinMax(metric: 'pvYield' | 'battery' | 'grid' | 'consumption'): { min: number; max: number } {
    if (!this.displaySolar.length) return { min: 0, max: 0 };
    const values = this.displaySolar.map(p => {
      switch (metric) {
        case 'pvYield':     return p.pvYieldW;
        case 'battery':     return -p.batteryDischargeW;
        case 'grid':        return p.gridConsumptionW;
        case 'consumption': return p.totalConsumptionW;
        default:            return 0;
      }
    });
    return { min: Math.min(...values), max: Math.max(...values) };
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  batteryRuntime(solar: SolarData): string {
    if (!solar.batteryCapacityWh) return '';
    if (solar.batteryDischargeW > 0) {
      const hours = (solar.batteryPercent / 100 * solar.batteryCapacityWh) / solar.batteryDischargeW;
      const label = this.translate.instant('AKKU_REST');
      if (hours < 1)  return `${label} ≈ < 1 h`;
      if (hours < 24) return `${label} ≈ ${Math.round(hours)} h`;
      const days = Math.round(hours / 24);
      return `${label} ≈ ${days} ${days === 1 ? this.translate.instant('AKKU_TAG') : this.translate.instant('AKKU_TAGE')}`;
    }
    if (solar.batteryDischargeW < 0) {
      const chargeW     = -solar.batteryDischargeW;
      const remainingWh = (100 - solar.batteryPercent) / 100 * solar.batteryCapacityWh;
      const hours       = remainingWh / chargeW;
      const label       = this.translate.instant('AKKU_VOLL');
      if (hours < 1/60) return `${label} ≈ < 1 min`;
      if (hours < 1)    return `${label} ≈ ${Math.round(hours * 60)} min`;
      return `${label} ≈ ${Math.round(hours)} h`;
    }
    return '';
  }
}
